import { X, FileText, File, Image as ImageIcon } from "lucide-react";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { cn } from "../../utils/utils";
import { http } from "../../utils/httpCommon";
import { toast } from "react-hot-toast";

const BASE_URL = process.env.REACT_APP_URL;

const getFileType = (fileUrl) => {
  if (typeof fileUrl === "string") {
    const extension = fileUrl.split(".").pop()?.toLowerCase();
    return extension;
  }
  return null;
};

const isImageFile = (fileUrl) => {
  const extension = getFileType(fileUrl);
  return ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension);
};

const getFileIcon = (fileUrl) => {
  const extension = getFileType(fileUrl);

  switch (extension) {
    case "pdf":
      return <FileText className="w-14 h-14 text-red-500" />;
    case "doc":
    case "docx":
      return <FileText className="w-14 h-14 text-blue-500" />;
    case "xls":
    case "xlsx":
      return <FileText className="w-14 h-14 text-green-500" />;
    case "txt":
      return <FileText className="w-14 h-14 text-gray-500" />;
    case "zip":
    case "rar":
      return <File className="w-14 h-14 text-purple-500" />;
    default:
      return <File className="w-14 h-14 text-gray-500" />;
  }
};

export const FileUploader = ({
  value = [],
  onChange,
  className,
  maxSize = 20 * 1024 * 1024, // 20MB default
  acceptedFileTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "application/zip",
    "application/x-rar-compressed",
  ],
  uploadFile,
  placeholder,
  supportingText,
  multiple = false,
  maxFiles = 5,
  layout = "grid",
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileUpload = async (file) => {
    if (uploadFile) {
      return await uploadFile(file);
    } else {
      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await http.postForm("/image", formData);

        if (!response.data || !response.data.url) {
          throw new Error("Upload failed: Invalid response");
        }

        return response.data.url;
      } catch (error) {
        console.error("File upload failed, falling back to preview:", error);
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(file);
        });
      }
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      if (multiple && value && value.length + acceptedFiles.length > maxFiles) {
        setError(`Max files reached: ${maxFiles}`);
        return;
      }

      setError(null);
      setIsUploading(true);

      try {
        const uploadPromises = acceptedFiles.map(async (file) => {
          if (file.size > maxSize) {
            throw new Error(`File size exceeded: ${file.name} (${file.size} bytes)`);
          }

          return await handleFileUpload(file);
        });

        const results = await Promise.all(uploadPromises);

        if (multiple) {
          onChange?.([...(value || []), ...results]);
        } else {
          onChange?.(results);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Upload failed";
        setError(errorMessage);
        console.error("Upload error:", err);
        toast.error(errorMessage);
      } finally {
        setIsUploading(false);
      }
    },
    [maxSize, onChange, multiple, value, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}),
    maxSize,
    maxFiles: multiple ? maxFiles - (value?.length || 0) : 1,
    disabled: isUploading || (multiple && value && value.length >= maxFiles),
  });

  const handleRemove = (e, index) => {
    e.stopPropagation();

    if (multiple && value) {
      const newValue = [...value];
      newValue.splice(index, 1);
      onChange?.(newValue.length > 0 ? newValue : []);
    } else {
      onChange?.([]);
    }

    setError(null);
  };

  const renderUploadZone = () => (
    <div
      {...getRootProps()}
      className={cn(
        "flex flex-col cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-gray-300 px-6 py-6 transition-colors duration-300",
        isDragActive ? "border-[#34B2C0] bg-[#34B2C0]/5" : "hover:border-[#34B2C0]/50",
        isUploading && "opacity-60 cursor-not-allowed",
        multiple && value && value.length >= maxFiles && "opacity-50 cursor-not-allowed"
      )}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center gap-1">
        <div className="h-10 w-10 text-[#34B2C0] flex items-center justify-center">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
        </div>
        <div className="flex flex-row flex-wrap items-center gap-1 mt-1">
          <p className="text-sm text-[#4B5563] font-medium">{placeholder || "Upload files"}</p>
          <span className="text-[#34B2C0] text-sm font-medium">Browse</span>
        </div>
        {!(value?.length > 0) && (
          <p className="text-xs text-[#6B7280] text-center">
            {multiple
              ? `${supportingText || "Supporting text"} (${value?.length || 0}/${maxFiles})`
              : supportingText || "Supporting text"}
          </p>
        )}
      </div>
    </div>
  );

  const renderFilePreview = (fileUrl, index) => {
    const isImage = isImageFile(fileUrl);

    return (
      <div
        key={index}
        className="relative rounded-lg overflow-hidden bg-white border border-gray-200"
        style={{ aspectRatio: "1/1" }}
      >
        {isImage ? (
          <div className="absolute inset-0">
            <img
              src={fileUrl.startsWith("http") ? fileUrl : `${BASE_URL}${fileUrl}`}
              alt="uploaded"
              className="w-full h-full aspect-square object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20"></div>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            {getFileIcon(fileUrl)}

            <p className="text-base text-gray-400 mt-1">{getFileType(fileUrl)?.toUpperCase()}</p>
          </div>
        )}
        <button
          onClick={(e) => handleRemove(e, index)}
          className="absolute top-2 right-2 rounded-full bg-red-500 text-white p-1 shadow-sm hover:bg-red-600 transition-colors"
          type="button"
          aria-label="Remove file"
        >
          <X size={16} className="text-white" />
        </button>
      </div>
    );
  };

  return (
    <div className={cn("w-full", className)}>
      <div
        className={cn(
          "grid gap-4",
          layout === "grid" && value?.length > 0
            ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
            : "grid-cols-1"
        )}
      >
        {value && value?.map((fileUrl, index) => renderFilePreview(fileUrl, index))}

        {(!(value && value?.length > 0) || multiple) && (
          <div
            className={cn(
              value && value.length > 0 ? "mt-0" : "mt-0",
              layout === "grid" ? "col-span-1" : "col-span-full"
            )}
          >
            {renderUploadZone()}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive mt-2">{error}</p>}
    </div>
  );
};

export const ImageUploader = FileUploader;
export default FileUploader;
